import React, { useEffect, useState, createContext, useContext } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Mail, Users, Lightbulb, AlertOctagon, Settings, LogOut, Menu, X, CircleDot } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';

const NAV = [
  { to: '/control-center/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/control-center/emails',    icon: Mail,            label: 'Correos' },
  { to: '/control-center/clients',   icon: Users,           label: 'Clientes' },
  { to: '/control-center/insights',  icon: Lightbulb,       label: 'Insights' },
  { to: '/control-center/errors',    icon: AlertOctagon,    label: 'Incidencias' },
  { to: '/control-center/settings',  icon: Settings,        label: 'Ajustes' },
];

// Simple shared context so children can read hotel + role and trigger refresh
const CCContext = createContext(null);
export const useCC = () => useContext(CCContext);

const ControlCenterLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [meCC, setMeCC] = useState(null);
  const [overview, setOverview] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadMe = async () => {
    try {
      const { data } = await api.get('/control-center/me');
      setMeCC(data);
    } catch {
      setMeCC(null);
    }
  };
  const loadOverview = async () => {
    try {
      const { data } = await api.get('/control-center/overview');
      setOverview(data);
    } catch {
      setOverview(null);
    }
  };
  useEffect(() => { loadMe(); loadOverview(); }, []);

  // Close mobile sidebar on route change
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const hotel = meCC?.hotel;
  const role = meCC?.hotel?.role || user?.role || 'viewer';
  // Prefer n8n's canonical name from /overview over the DB-seeded name.
  const displayHotelName = overview?.hotel?.name || hotel?.name || '—';
  const automationActive = overview?.hotel?.automation_active;

  return (
    <CCContext.Provider value={{ hotel, role, overview, refreshMe: loadMe, refreshOverview: loadOverview }}>
      <div className="min-h-screen bg-slate-50 flex">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-slate-200 flex-col">
          <div className="h-16 flex items-center px-6 border-b border-slate-200">
            <img src="/aivanilo-logo.png" alt="Aivanilo" className="h-7 w-auto" />
          </div>
          <nav className="flex-1 p-3 space-y-1" aria-label="Navegación Control Center">
            {NAV.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
                  }`
                }
              >
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-slate-200">
            <button
              onClick={handleLogout}
              data-testid="cc-logout"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Cerrar sesión
            </button>
          </div>
        </aside>

        {/* Sidebar (mobile drawer) */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-40" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-slate-900/50" onClick={() => setSidebarOpen(false)} />
            <aside className="absolute left-0 top-0 h-full w-72 bg-white flex flex-col shadow-xl">
              <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
                <img src="/aivanilo-logo.png" alt="Aivanilo" className="h-7 w-auto" />
                <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-slate-100" aria-label="Cerrar menú">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex-1 p-3 space-y-1">
                {NAV.map(({ to, icon: Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`nav-mobile-${label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                        isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </NavLink>
                ))}
              </nav>
              <div className="p-3 border-t border-slate-200">
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100">
                  <LogOut className="w-4 h-4" /> Cerrar sesión
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* Main column */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <header className="bg-white border-b border-slate-200 h-16 flex items-center px-4 lg:px-8 gap-3 sticky top-0 z-20">
            <button
              onClick={() => setSidebarOpen(true)}
              data-testid="cc-mobile-menu"
              aria-label="Abrir menú"
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-slate-900 font-semibold truncate" data-testid="topbar-hotel-name">
                <span className="truncate">{displayHotelName}</span>
              </div>
              <StatusBadge active={automationActive} inline />
            </div>
            <div className="hidden sm:flex flex-col items-end text-right min-w-0">
              <span className="text-sm font-medium text-slate-900 truncate max-w-[220px]" data-testid="topbar-user-email">{user?.email}</span>
              <span className="text-xs text-slate-500 capitalize">{role}</span>
            </div>
            <button
              onClick={handleLogout}
              className="ml-2 hidden sm:inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100"
              data-testid="cc-logout-top"
              aria-label="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </header>

          <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8">
            <Outlet />
          </main>
        </div>
      </div>
    </CCContext.Provider>
  );
};

// Small header helper reused
export const StatusBadge = ({ active, inline = false }) => {
  if (active === undefined || active === null) return null;
  const color = active ? 'text-green-700 bg-green-50 border-green-200' : 'text-amber-700 bg-amber-50 border-amber-200';
  const dot   = active ? 'bg-green-500' : 'bg-amber-500';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full border ${color} ${inline ? 'mt-0.5' : ''}`} data-testid="automation-badge">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {active ? 'Automatización activa' : 'Automatización pausada'}
    </span>
  );
};

export default ControlCenterLayout;
